import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../contexts/translationContext';
import { fetcher } from '../../apiUtils';
import { API_URL } from '../../apiTypes';
import useSWR from 'swr';
import { Loader } from '../../components/Loader/Loader';
import { Grid } from '@material-ui/core';
import { AuditType } from '../../types/AuditTypeApiResponse';
import { useStyles } from './styles';
import { VsmApiResponse } from '../../types/VsmApiResponse';
import { Production, ProductionApiResponse } from '../../types/ProductionApiResponse';
import { useLocation } from 'react-router';
import { Task, Solver, TaskState } from '../Tasks/types';
import { FilterParams, ReportTasksFilter } from './ReportTasksFilter';
import { ReportHeader } from '../../components/ReportHeader/ReportHeader';
import { Zone } from '../../types/ZoneApiResponse';
import { PyramidOfLossesReport } from './types';
import './styles.scss';

export interface TaskAnswerData {
    task: Task;
    attachments: any[];
}

export const ReportPyramidOfLosses = () => {
    const classes = useStyles();
    const { t } = useTranslation();

    const location = useLocation();

    const parseUrlParamsToFilterParams = (
        params: string,
        currentFiscalYear?: number
    ): FilterParams => {
        const paramsArr = params.split('&');
        let _vsmIds: number[] = [];
        let _productionIds: number[] = [];
        let _zoneIds: number[] = [];
        let _fiscalYear: number | null = null;
        paramsArr.forEach((param) => {
            const paramArr = param.split('=');
            switch (paramArr[0]) {
                case 'fiscalYear':
                    _fiscalYear = Number(paramArr[1]);
                    break;
                case 'vsmIdArr':
                    _vsmIds = [..._vsmIds, Number(paramArr[1])];
                    break;
                case 'productionIdArr':
                    _productionIds = [..._productionIds, Number(paramArr[1])];
                    break;
                case 'zoneIdArr':
                    _zoneIds = [..._zoneIds, Number(paramArr[1])];
                    break;
            }
        });

        return {
            fiscalYearsId: !currentFiscalYear
                ? null
                : _fiscalYear === 'current' || !_fiscalYear
                ? currentFiscalYear
                : Number(_fiscalYear),
            vsmIds: _vsmIds,
            productionIds: _productionIds,
            zoneIds: _zoneIds,
        };
    };

    const [filterParams, setFilterParams] = useState<FilterParams | null>(null);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState<number | null>(null);

    const [filteredProductionList, setFilteredProductionList] = useState<Production[]>([]);
    const [filteredZoneList, setFilteredZoneList] = useState<Zone[]>([]);

    // Fiscal year
    const { data: fiscalYearsList } = useSWR<number[]>(
        `${API_URL.COMMON_GET_FISCAL_YEARS}`,
        fetcher
    );
    // VSM list
    const { data: vsmList } = useSWR<VsmApiResponse>(`${API_URL.VSM_LIST}`, fetcher);
    // Production list
    const { data: productionList } = useSWR<ProductionApiResponse>(
        `${API_URL.PRODUCTION_LIST}`,
        fetcher
    );
    // Zone list
    const { data: zoneList } = useSWR<Zone[]>(`${API_URL.ZONES_LIST}`, fetcher);

    const getReportParams = () => {
        if (location.search === '') return '';

        return location.search
            .replace('?&', '?')
            .replace('fiscalYear=current&', '')
            .replace('fiscalYear=current', '');
    };

    const { data: report } = useSWR<PyramidOfLossesReport>(
        `${API_URL.GET_REPORT_4}${getReportParams()}`,
        fetcher
    );

    useEffect(() => {
        if (!fiscalYearsList) return;
        const currentFiscalYear = fiscalYearsList[0];
        setFilterParams(parseUrlParamsToFilterParams(location.search, currentFiscalYear));
    }, [fiscalYearsList]);

    useEffect(() => {
        if (!productionList || !filterParams) return;
        let tmpFilteredProdList = productionList;
        if (filterParams.vsmIds.length !== 0) {
            tmpFilteredProdList = productionList.filter(
                (prod: Production) =>
                    filterParams.vsmIds.some((id: number) => id === prod.parentVsmId) ?? prod
            );
        }
        setFilteredProductionList(tmpFilteredProdList);
    }, [productionList]);

    useEffect(() => {
        if (!zoneList || !filterParams) return;
        let tmpFiltered = zoneList;
        if (filteredProductionList.length > 0) {
            tmpFiltered = tmpFiltered.filter((x) =>
                filteredProductionList.map((x) => x.id).includes(x.parentProductionId)
            );
        }

        if (filterParams.productionIds.length != 0) {
            tmpFiltered = tmpFiltered.filter((x) =>
                filterParams.productionIds.includes(x.parentProductionId)
            );
        }

        setFilteredZoneList(tmpFiltered);
    }, [zoneList, filteredProductionList]);

    // Volá se při změně filtru. Nastaví URL parametry. Čímž dojde k refreshy.
    const filterChanged = (filterParams: FilterParams) => {
        let urlQuery = '';
        if (!fiscalYearsList) return;

        const currentFiscalYear = fiscalYearsList[0];
        if (currentFiscalYear === Number(filterParams.fiscalYearsId))
            urlQuery += `&fiscalYear=current`;
        else if (filterParams.fiscalYearsId)
            urlQuery += `&fiscalYear=${filterParams.fiscalYearsId}`;

        // vsm
        filterParams.vsmIds.forEach((item) => {
            urlQuery += `&vsmIdArr=${item}`;
        });
        // productions
        filterParams.productionIds.forEach((item) => {
            urlQuery += `&productionIdArr=${item}`;
        });
        // zones
        filterParams.zoneIds.forEach((item) => {
            urlQuery += `&zoneIdArr=${item}`;
        });
        window.location.search = urlQuery;
    };

    if (!vsmList || !productionList || !zoneList || !fiscalYearsList || !filterParams)
        return <Loader />;

    return (
        <Grid container>
            <ReportHeader title={t('Report.pyramid-of-losses.main-headline')} disableBorder />
            {/* left panel */}
            <Grid item xs={12} lg={2} style={{ padding: 30 }}>
                <ReportTasksFilter
                    fiscalYears={fiscalYearsList}
                    productionList={filteredProductionList}
                    vsmList={vsmList}
                    zoneList={filteredZoneList}
                    defaultFilterParams={filterParams}
                    onFilterChange={filterChanged}
                    onFiscalYearChange={(year: number | null) => setSelectedFiscalYear(year)}
                />
            </Grid>

            {/* mid panel */}
            <Grid item xs={12} lg={10} style={{ padding: 30 }}>
                {report && (
                    <div className="chart-container">
                        <div className="chart-rows">
                            <div className="row">
                                <div className="target-value" style={{ top: 30, right: 5 }}>
                                    0
                                </div>
                                <div className="label" style={{ paddingLeft: 70 }}>
                                    Vážné nebo smrtelné poranění
                                </div>
                                <div className="state red">
                                    <img src="/carousel/green_dot.png" />
                                </div>
                                <div className="reality-value">0</div>
                            </div>
                            <div className="row" style={{ marginTop: 4 }}>
                                <div className="target-value" style={{ top: 40, right: 5 }}>
                                    0
                                </div>
                                <div className="label" style={{ paddingLeft: 140 }}>
                                    Drobné poranění
                                </div>
                                <div className="state red">
                                    <img src="/carousel/green_dot.png" />
                                </div>
                                <div className="reality-value">0</div>
                            </div>
                            <div className="row" style={{ marginTop: 4 }}>
                                <div className="target-value" style={{ top: 60, right: 5 }}>
                                    0
                                </div>
                                <div className="label" style={{ paddingLeft: 220 }}>
                                    Ztráta na majetku
                                </div>
                                <div className="state red">
                                    <img src="/carousel/green_dot.png" />
                                </div>
                                <div className="reality-value">0</div>
                            </div>
                            <div className="row" style={{ marginTop: 3 }}>
                                <div className="target-value" style={{ top: 80, right: 5 }}>
                                    {report.safetyTargetTwo}
                                </div>
                                <div className="label" style={{ paddingLeft: 290 }}>
                                    {report.safetyLabelTwo}
                                </div>
                                <div className="state red">
                                    {report.safetyRealityTwo < report.safetyTargetTwo ? (
                                        <img src="/carousel/red_dot.png" />
                                    ) : (
                                        <img src="/carousel/green_dot.png" />
                                    )}
                                </div>
                                <div className="reality-value">{report.safetyRealityTwo}</div>
                            </div>
                            <div className="row" style={{ marginTop: 4 }}>
                                <div className="target-value" style={{ top: 105, right: 5 }}>
                                    {report.safetyTargetOne}
                                </div>
                                <div className="label" style={{ paddingLeft: 370 }}>
                                    {report.safetyLabelOne}
                                </div>
                                <div className="state red">
                                    {report.safetyRealityOne < report.safetyTargetOne ? (
                                        <img src="/carousel/red_dot.png" />
                                    ) : (
                                        <img src="/carousel/green_dot.png" />
                                    )}
                                </div>
                                <div className="reality-value">
                                    <div>{report.safetyRealityOne}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Grid>
        </Grid>
    );
};
